/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { initializePaddle, CheckoutEventNames } from '@paddle/paddle-js';

const PaddleContext = createContext(null);

export function PaddleProvider({ children }) {
  const [paddle, setPaddle] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [lastCompletedTransaction, setLastCompletedTransaction] = useState(null);

  // Use ref for callback to avoid re-initializing Paddle
  const onCheckoutCompleteRef = useRef(null);

  // Initialize Paddle on mount
  useEffect(() => {
    const clientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
    const environment = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox';

    console.log('[Paddle] Initializing with environment:', environment);

    if (!clientToken) {
      console.warn('Paddle client token not configured');
      return;
    }

    initializePaddle({
      token: clientToken,
      environment: environment,
      eventCallback: (event) => {
        switch (event.name) {
          case CheckoutEventNames.CHECKOUT_LOADED:
            console.log('Paddle checkout loaded');
            break;

          case CheckoutEventNames.CHECKOUT_COMPLETED:
            console.log('Paddle checkout completed:', event.data?.transaction_id);
            setCheckoutOpen(false);
            setLastCompletedTransaction(event.data?.transaction_id);

            // Broadcast global event for decoupled listeners (UserContext, etc)
            window.dispatchEvent(new CustomEvent('paddle:checkout-completed', {
              detail: event.data
            }));

            // Call the registered callback if any (Legacy support)
            if (onCheckoutCompleteRef.current) {
              onCheckoutCompleteRef.current(event.data);
            }
            break;

          case CheckoutEventNames.CHECKOUT_CLOSED:
            console.log('Paddle checkout closed');
            setCheckoutOpen(false);
            break;

          case CheckoutEventNames.CHECKOUT_ERROR:
            console.error('Paddle checkout error:', event.error);
            break;
        }
      }
    })
      .then((paddleInstance) => {
        if (paddleInstance) {
          setPaddle(paddleInstance);
          setIsReady(true);
          console.log('Paddle initialized successfully');
        }
      })
      .catch((err) => {
        console.error('Failed to initialize Paddle:', err);
      });
  }, []);

  // Allow components to register a callback for checkout completion
  const setOnCheckoutComplete = useCallback((callback) => {
    onCheckoutCompleteRef.current = callback;
  }, []);

  // Open checkout for subscription
  const openSubscriptionCheckout = useCallback((userId, userEmail) => {
    if (!paddle) {
      console.error('Paddle not initialized');
      return;
    }

    const priceId = import.meta.env.VITE_PADDLE_PRO_PRICE_ID;
    if (!priceId) {
      console.error('Pro subscription price ID not configured');
      return;
    }

    console.log('[Paddle] Opening subscription checkout', {
      priceId,
      user_id: userId,
      email: userEmail
    });

    if (!userId) {
      console.warn('[Paddle] Warning: No user_id provided for checkout. Webhook may fail to identify user.');
    }

    setCheckoutOpen(true);

    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: userEmail ? { email: userEmail } : undefined,
      customData: {
        user_id: userId
      },
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        successUrl: `${window.location.origin}/subscription/success`
      }
    });
  }, [paddle]);

  // Open checkout for credit pack (future use)
  const openCreditPackCheckout = useCallback((userId, userEmail, packPriceId) => {
    if (!paddle) {
      console.error('Paddle not initialized');
      return;
    }

    setCheckoutOpen(true);

    paddle.Checkout.open({
      items: [{ priceId: packPriceId, quantity: 1 }],
      customer: userEmail ? { email: userEmail } : undefined,
      customData: {
        user_id: userId
      },
      settings: {
        displayMode: 'overlay',
        theme: 'light',
        successUrl: `${window.location.origin}/purchase/success`
      }
    });
  }, [paddle]);

  // Close checkout
  const closeCheckout = useCallback(() => {
    if (paddle) {
      paddle.Checkout.close();
      setCheckoutOpen(false);
    }
  }, [paddle]);

  // Get price preview for displaying prices
  const getPricePreview = useCallback(async (items) => {
    if (!paddle) {
      return null;
    }

    // Support single item (legacy) or array of items (batch)
    const requestItems = Array.isArray(items)
      ? items
      : [{ priceId: items, quantity: 1 }];

    try {
      console.log('Fetching price preview for:', requestItems);
      const preview = await paddle.PricePreview({
        items: requestItems
      });
      return preview;
    } catch (err) {
      console.error('Failed to get price preview:', err);
      // Log the payload that failed
      console.error('Failed payload:', JSON.stringify({ items: requestItems }));
      return null;
    }
  }, [paddle]);

  const value = useMemo(() => ({
    paddle,
    isReady,
    checkoutOpen,
    lastCompletedTransaction,
    openSubscriptionCheckout,
    openCreditPackCheckout,
    closeCheckout,
    getPricePreview,
    setOnCheckoutComplete
  }), [paddle, isReady, checkoutOpen, lastCompletedTransaction, openSubscriptionCheckout, openCreditPackCheckout, closeCheckout, getPricePreview, setOnCheckoutComplete]);

  return (
    <PaddleContext.Provider value={value}>
      {children}
    </PaddleContext.Provider>
  );
}

export function usePaddle() {
  const context = useContext(PaddleContext);
  if (!context) {
    throw new Error('usePaddle must be used within a PaddleProvider');
  }
  return context;
}
